import type { AudioSystem, GameEngine } from "@bastion/game-core";
import type { GameContent } from "@bastion/data";

/** 游戏中使用到的音效标识符常量。 */
export const SfxId = {
  buildTower: "build_tower",
  upgradeTower: "upgrade_tower",
  killEnemy: "kill_enemy",
  killBoss: "kill_boss",
  waveComplete: "wave_complete",
  gameWon: "game_won",
  gameLost: "game_lost",
  skillUsed: "skill_used",
  skillFire: "skill_fire",
  skillFrost: "skill_frost",
  towerArrow: "tower_arrow",
  towerCannon: "tower_cannon",
  towerThunder: "tower_thunder",
  towerIce: "tower_ice",
} as const;

export type SfxId = (typeof SfxId)[keyof typeof SfxId];

const TOWER_SFX_MAP: Record<string, SfxId> = {
  arrow: SfxId.towerArrow,
  cannon: SfxId.towerCannon,
  thunder_tower: SfxId.towerThunder,
  ice_tower: SfxId.towerIce,
};

/** 订阅单局的音效事件。返回值必须随对应 Engine 生命周期释放。 */
export function subscribeGameAudioEvents(
  engine: GameEngine,
  audio: AudioSystem,
  content: GameContent,
): () => void {
  const play = (sound: SfxId) => audio.playSound(sound);
  const unsubs = [
    engine.on("tower_built", () => play(SfxId.buildTower)),
    engine.on("tower_upgraded", () => play(SfxId.upgradeTower)),
    engine.on("enemy_killed", (event) => {
      const isBoss = event.payload?.["isBoss"] === true;
      play(isBoss ? SfxId.killBoss : SfxId.killEnemy);
    }),
    engine.on("wave_completed", () => play(SfxId.waveComplete)),
    engine.on("game_won", () => play(SfxId.gameWon)),
    engine.on("game_lost", () => play(SfxId.gameLost)),
    engine.on("skill_used", (event) => {
      const skillId = event.payload?.["skillId"];
      if (skillId === "slow_all") play(SfxId.skillFrost);
      else if (skillId !== "area_damage") play(SfxId.skillUsed);
    }),
    engine.on("global_skill_impact", (event) => {
      if (event.payload?.["skillId"] === "area_damage") play(SfxId.skillFire);
    }),
    engine.on("attack_started", (event) => {
      const sourceId = event.payload?.["sourceId"];
      if (typeof sourceId !== "string") return;
      const tower = engine.state.towers.find((candidate) => candidate.id === sourceId);
      if (!tower || !content.towers[tower.type]) return;
      const sound = TOWER_SFX_MAP[tower.type];
      if (sound) play(sound);
    }),
  ];
  let disposed = false;
  return () => {
    if (disposed) return;
    disposed = true;
    for (const unsubscribe of unsubs) unsubscribe();
  };
}
